import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { fetchSystemNotices, isSystemNoticesAvailable, markAllNoticesRead } from '../api'
import type { SystemNotice } from '../types'

export const systemNoticesKey = ['notifications', 'system-notices'] as const

interface UseSystemNoticesResult {
  notices: SystemNotice[]
  unreadCount: number
  isLoading: boolean
  /** The API cannot answer yet — a different state from "answered with none". */
  isUnavailable: boolean
  isError: boolean
  retry: () => void
  markAllRead: () => void
  isMarkingRead: boolean
}

/**
 * The system-notice feed behind the app-bar bell.
 *
 * Notices arrive while the merchant is elsewhere, so the query refetches when
 * the tab regains focus rather than holding whatever was true at mount. There
 * is no polling: a bell that reloads on a timer costs a request a minute for a
 * feed that changes a few times a week.
 */
export function useSystemNotices(): UseSystemNoticesResult {
  const queryClient = useQueryClient()

  const { data, isPending, isError, refetch } = useQuery({
    queryKey: systemNoticesKey,
    queryFn: fetchSystemNotices,
    enabled: isSystemNoticesAvailable,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  })

  const markRead = useMutation({
    mutationFn: markAllNoticesRead,
    // Written straight into the cache: the panel is open and looking at the
    // rows, and a refetch would blank them for the length of a round trip to
    // learn something we already know.
    onSuccess: () => {
      const seenAt = new Date().toISOString()

      queryClient.setQueryData(systemNoticesKey, (current: typeof data) =>
        current
          ? {
              unreadCount: 0,
              notices: current.notices.map((notice) => ({
                ...notice,
                readAt: notice.readAt ?? seenAt,
              })),
            }
          : current,
      )
    },
  })

  return {
    notices: data?.notices ?? [],
    unreadCount: data?.unreadCount ?? 0,
    // A disabled query sits in `pending` forever. Reporting that as loading
    // would leave the panel shimmering for a request that is never sent.
    isLoading: isSystemNoticesAvailable && isPending && !isError,
    isUnavailable: !isSystemNoticesAvailable,
    isError,
    retry: () => void refetch(),
    markAllRead: () => markRead.mutate(),
    isMarkingRead: markRead.isPending,
  }
}
